import authSession from "../../lib/authSession";
import { API_URL } from "../../constants";
import { safeFetch, explainApiError } from "../../utils/toDisplayString";

export const resourcePersonBookInitialState = {
  books: [],
  booksLoading: false,
  booksError: null,
  booksTotal: 0,
  booksPages: 1,
  bookDetail: null,
  bookDetailLoading: false,
  bookDetailError: null,
  bookDownloads: [],
  bookDownloadsLoading: false,
  bookDownloadsError: null,
  bookAnalytics: null,
  bookAnalyticsLoading: false,
  bookAnalyticsError: null,
  bookMutationLoading: false,
  bookMutationError: null,
};

function buildUrl(path, params = {}) {
  const query = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== "")).toString();
  return `${API_URL.replace(/\/$/, "")}${path}${query ? `?${query}` : ""}`;
}

async function request(path, { method = "GET", params, body } = {}) {
  if (!API_URL) throw new Error("REACT_APP_API_BASE_URL is not configured.");
  const token = authSession.getToken();
  const response = await safeFetch(buildUrl(path, params), {
    method,
    headers: { Accept: "application/json", ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: "omit",
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const text = await response.text();
  let data = {};
  if (text) {
    try { data = JSON.parse(text); } catch { throw new Error(`Books API returned a non-JSON response (${response.status}).`); }
  }
  if (!response.ok || data.error || data.success === false) throw new Error(explainApiError(response.status, data || {}, `Request failed with status ${response.status}.`));
  return data?.data && typeof data.data === "object" ? data.data : data;
}

function pageOf(data) {
  const items = Array.isArray(data) ? data : data?.items || data?.books || [];
  return { items, total: data?.total ?? data?.totalItems ?? items.length, pages: data?.totalPages ?? data?.pages ?? 1 };
}

function listOf(data, keys) {
  if (Array.isArray(data)) return data;
  for (const key of keys) if (Array.isArray(data?.[key])) return data[key];
  return [];
}

function bookOf(data) {
  return data?.book && typeof data.book === "object" ? data.book : data;
}

export function createResourcePersonBookActions(set) {
  const mutation = async (path, method, body) => {
    set({ bookMutationLoading: true, bookMutationError: null });
    try {
      const book = bookOf(await request(path, { method, body }));
      set({ bookMutationLoading: false, bookDetail: book });
      return { success: true, book };
    } catch (error) {
      set({ bookMutationLoading: false, bookMutationError: error.message });
      return { success: false, error: error.message };
    }
  };

  return {
    fetchBooks: async (params = {}) => {
      set({ booksLoading: true, booksError: null });
      try {
        const result = pageOf(await request("/api/resource-person/books", { params }));
        set({ books: result.items, booksTotal: result.total, booksPages: result.pages, booksLoading: false });
        return { success: true, ...result };
      } catch (error) {
        set({ books: [], booksLoading: false, booksError: error.message });
        return { success: false, error: error.message };
      }
    },
    createBook: (payload) => mutation("/api/resource-person/books", "POST", payload),
    fetchBookById: async (id) => {
      set({ bookDetailLoading: true, bookDetailError: null });
      try { const book = bookOf(await request(`/api/resource-person/books/${id}`)); set({ bookDetail: book, bookDetailLoading: false }); return { success: true, book }; }
      catch (error) { set({ bookDetail: null, bookDetailLoading: false, bookDetailError: error.message }); return { success: false, error: error.message }; }
    },
    updateBook: (id, payload) => mutation(`/api/resource-person/books/${id}`, "PATCH", payload),
    submitBook: (id) => mutation(`/api/resource-person/books/${id}/submit`, "PATCH"),
    unsubmitBook: (id) => mutation(`/api/resource-person/books/${id}/unsubmit`, "PATCH"),
    deleteBook: (id) => mutation(`/api/resource-person/books/${id}`, "DELETE"),
    fetchBookDownloads: async (id, params = {}) => {
      set({ bookDownloadsLoading: true, bookDownloadsError: null });
      try { const data = await request(`/api/resource-person/books/${id}/downloads`, { params }); const items = listOf(data, ["items", "downloads"]); set({ bookDownloads: items, bookDownloadsLoading: false }); return { success: true, downloads: items }; }
      catch (error) { set({ bookDownloads: [], bookDownloadsLoading: false, bookDownloadsError: error.message }); return { success: false, error: error.message }; }
    },
    fetchBookAnalytics: async (id) => {
      set({ bookAnalyticsLoading: true, bookAnalyticsError: null });
      try { const analytics = await request(`/api/resource-person/books/${id}/analytics`); set({ bookAnalytics: analytics, bookAnalyticsLoading: false }); return { success: true, analytics }; }
      catch (error) { set({ bookAnalytics: null, bookAnalyticsLoading: false, bookAnalyticsError: error.message }); return { success: false, error: error.message }; }
    },
  };
}
